import { create } from 'zustand';
import { 
    sanctionsApiService, 
    AmlSanction, 
    SanctionsList, 
    NilListEntry 
} from '../services/api/sanctions/SanctionsApiService';
import { utils } from '../utils/utils';

interface NilWhitelistState {
    isLoading: boolean;
    sanctionsLists: SanctionsList[];
    checkedLists: Set<string>;
    loadError: string | null;
    isSubmitting: boolean;
    submitError: string | null;
    detailedJson: any | null;
}

interface WhitelistParams {
    cbsId: string;
    watchlistEntryIds: string[];
    reason: string;
    attachment: File;
}

interface NilWhitelistActions {
    loadSanctions: (cbsId: string) => Promise<void>;
    toggleCheckbox: (listId: string, checked: boolean) => void;
    submitWhitelist: (params: WhitelistParams) => Promise<boolean>;
    reset: () => void;
}

type NilWhitelistStore = NilWhitelistState & NilWhitelistActions;

const INITIAL_STATE: NilWhitelistState = {
    isLoading: false,
    sanctionsLists: [],
    checkedLists: new Set(),
    loadError: null,
    isSubmitting: false,
    submitError: null,
    detailedJson: null,
};

const EMPTY_STATE: Pick<NilWhitelistState, 'sanctionsLists' | 'checkedLists' | 'detailedJson'> = {
    sanctionsLists: [],
    checkedLists: new Set(),
    detailedJson: null,
};

const is404Error = (error: any): boolean => {
    if (error?.status === 404 || error?.response?.status === 404) {
        return true;
    }

    if (error?.code === 'ERR_BAD_REQUEST' && error?.response?.status === 404) {
        return true;
    }

    const message = error?.message || error?.response?.data || error || '';
    const messageStr = typeof message === 'string' ? message : JSON.stringify(message);
    
    if (messageStr.includes('404') || 
        messageStr.includes('"status":404') || 
        messageStr.includes('"status": 404')) {
        return true;
    }

    if (typeof message === 'object' && message?.status === 404) {
        return true;
    }

    return false;
};

const getErrorMessage = (error: any, defaultMessage: string): string => {
    const message = error?.message || error?.response?.data || error;
    return typeof message === 'string' ? message : JSON.stringify(message || {});
};

const transformToSanctionsList = (raw: any): SanctionsList[] => {
    if (raw?.nilLists && Array.isArray(raw.nilLists)) {
        return raw.nilLists.map((entry: NilListEntry) => ({
            id: entry.watchlistEntryId,
            name: entry.sanctionCategory
                ? `${entry.sanctionCategory} - ${entry.watchlistEntryId}`
                : entry.watchlistEntryId,
            data: entry.rabbitResponseJson ?? entry
        }));
    }

    const apiItems = (
        Array.isArray(raw?.value) ? raw.value : 
        Array.isArray(raw) ? raw : 
        []
    ) as AmlSanction[];

    return apiItems.map((entry: AmlSanction) => ({
        id: entry.watchlistEntryId,
        name: entry.sanctionCategory
            ? `${entry.sanctionCategory} - ${entry.watchlistEntryId}`
            : entry.watchlistEntryId,
        data: entry.jsonResponse ?? entry
    }));
};

const sortSanctionsList = (lists: SanctionsList[]): SanctionsList[] => {
    return [...lists].sort((a, b) => a.name.localeCompare(b.name));
};

const createCheckedSet = (lists: SanctionsList[]): Set<string> => {
    return new Set(lists.map(list => list.id));
};

export const useNilWhitelistStore = create<NilWhitelistStore>((set, get) => ({
    ...INITIAL_STATE,

    loadSanctions: async (cbsId: string) => {
        if (!cbsId) {
            set({ 
                ...EMPTY_STATE,
                loadError: null,
                isLoading: false 
            });
            return;
        }

        set({ isLoading: true, loadError: null });

        try {
            const response = await sanctionsApiService.getSanctions(cbsId, "Blocked");

            if (!response.success) {
                const errorMessage = response.message || "Failed to load sanctions lists";

                if (is404Error(errorMessage)) {
                    set({
                        ...EMPTY_STATE,
                        isLoading: false,
                        loadError: null
                    });
                    return;
                }

                set({
                    ...EMPTY_STATE,
                    isLoading: false,
                    loadError: errorMessage
                });
                return;
            }

            const raw = response.data as any;
            const sanctionsLists = transformToSanctionsList(raw);
            const sortedLists = sortSanctionsList(sanctionsLists);
            const checkedLists = createCheckedSet(sortedLists);
            const detailedJson = raw?.detailedJson || null;

            set({
                isLoading: false,
                sanctionsLists: sortedLists,
                checkedLists,
                loadError: null,
                detailedJson
            });

        } catch (error: any) {
            if (is404Error(error)) {
                set({
                    ...EMPTY_STATE,
                    isLoading: false,
                    loadError: null
                });
                return;
            }

            const errorMessage = getErrorMessage(
                error, 
                "Failed to load sanctions lists. Please try again."
            );

            set({
                ...EMPTY_STATE,
                isLoading: false,
                loadError: errorMessage
            });
        }
    },

    toggleCheckbox: (listId: string, checked: boolean) => {
        set(state => {
            const checkedLists = new Set(state.checkedLists);
            
            if (checked) {
                checkedLists.add(listId);
            } else {
                checkedLists.delete(listId);
            }

            return { checkedLists };
        });
    },

    submitWhitelist: async ({ cbsId, watchlistEntryIds, reason, attachment }) => {
        set({ isSubmitting: true, submitError: null });

        try {
            const fileData = await utils.fileToBase64(attachment);

            const response = await sanctionsApiService.whitelistCustomer({
                cbsId,
                watchlistEntryIds,
                reason,
                fileName: attachment.name,
                fileData
            });

            if (!response.success) {
                const errorMessage = response.message || "Whitelist action failed. Please try again.";
                set({
                    isSubmitting: false,
                    submitError: errorMessage
                });
                return false;
            }

            set({ 
                isSubmitting: false, 
                submitError: null 
            });
            return true;

        } catch (error: any) {
            const errorMessage = getErrorMessage(
                error,
                "Whitelist action failed. Please try again."
            );

            set({
                isSubmitting: false,
                submitError: errorMessage
            });
            return false;
        }
    },

    reset: () => {
        const { sanctionsLists } = get();
        const checkedLists = createCheckedSet(sanctionsLists);

        set({
            checkedLists,
            submitError: null
        });
    },
}));
